import {
  composeWithDevTools,
  composeWithDevToolsDevelopmentOnly,
} from '@redux-devtools/extension';
import Router from 'next/router';
import type { AnyAction, Store } from 'redux';
import { applyMiddleware, combineReducers, createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { all, fork } from 'redux-saga/effects';
import type { StateType } from 'typesafe-actions';

import { accountReducer as AccountsReducer } from '@portal/store/src/account/reducer';
import { accountSaga } from '@portal/store/src/account/sagas';
import type { AccountState } from '@portal/store/src/account/types';
import { analyticsReducer as AnalyticsReducer } from '@portal/store/src/analytics/reducer';
import { analyticsSaga } from '@portal/store/src/analytics/sagas';
import type { AnalyticsState } from '@portal/store/src/analytics/types';
import {
  configurationReducer as ConfigReducer,
  initialConfigState,
} from '@portal/store/src/configuration/reducer';
import type { ConfigState } from '@portal/store/src/configuration/types';
import { demandReportingReducer } from '@portal/store/src/demand-reporting/reducer';
import { demandReportingSaga } from '@portal/store/src/demand-reporting/sagas';
import { featureFlagReducer as FeatureFlagReducer } from '@portal/store/src/feature-flag/reducer';
import { featureFlagSaga } from '@portal/store/src/feature-flag/sagas';
import { feedbackReducer as FeedbackReducer } from '@portal/store/src/feedback/reducer';
import { feedbackSaga } from '@portal/store/src/feedback/sagas';
import type { FeedbackState } from '@portal/store/src/feedback/types';
import { leadReducer as LeadReducer } from '@portal/store/src/lead/reducer';
import { leadSaga } from '@portal/store/src/lead/sagas';
import type { LeadState } from '@portal/store/src/lead/types';
import { leadHydrationReducer as LeadHydrationReducer } from '@portal/store/src/lead-hydration/reducer';
import { leadHydrationSaga } from '@portal/store/src/lead-hydration/sagas';
import { loadingReducer } from '@portal/store/src/loading/reducer';
import { notificationReducer as NotificationReducer } from '@portal/store/src/notifications/reducer';
import { notificationsSagas } from '@portal/store/src/notifications/sagas';
import type { NotificationState } from '@portal/store/src/notifications/types';
import { onboardingContractReducer as OnboardingContractReducer } from '@portal/store/src/onboarding-contract/reducer';
import { onboardingContractSaga } from '@portal/store/src/onboarding-contract/sagas';
import type { OnboardingContractState } from '@portal/store/src/onboarding-contract/types';
import { originatorExternalReducer } from '@portal/store/src/originator/reducer';
import { originatorExternalSaga } from '@portal/store/src/originator/sagas';
import { payableReducer as PayablesReducer } from '@portal/store/src/payable/reducer';
import { payableSaga } from '@portal/store/src/payable/sagas';
import type { PayableState } from '@portal/store/src/payable/types';
import { pricingControlsReducer as PricingControlsReducer } from '@portal/store/src/pricing-controls/reducer';
import { pricingControlsSaga } from '@portal/store/src/pricing-controls/sagas';
import type { PricingControlsState } from '@portal/store/src/pricing-controls/types';
import type {
  DemandReportingState,
  FeatureFlagState,
  LeadHydrationState,
  LoadingState,
  OriginatorExternalState,
  SupplyMigrationState,
} from '@portal/store/src/types';
import { userReducer as UserReducer } from '@portal/store/src/user/reducer';
import { usersSaga } from '@portal/store/src/user/sagas';
import type { UserState } from '@portal/store/src/user/types';
import { whitelistReducer as WhitelistReducer } from '@portal/store/src/whitelist/reducer';
import { whitelistSaga } from '@portal/store/src/whitelist/sagas';
import type { WhitelistState } from '@portal/store/src/whitelist/types';

import {
  authenticationReducer,
  authenticationSaga,
} from '@/store/authentication';
import type { AuthenticationState } from '@/store/authentication';
import { complianceSaga } from '@/store/compliance/sagas';
import type { ComplianceState } from '@/store/compliance/types';
import { dataImportsSaga } from '@/store/data-imports/sagas';
import type { DataImportsState } from '@/store/data-imports/types';
import { homeSaga } from '@/store/home/sagas';
import { importedChannelEventsSaga } from '@/store/imported-channel-events/sagas';
import type { ImportedChannelEventsState } from '@/store/imported-channel-events/types';
import { offerRulesSaga } from '@/store/originator/sagas/offer-rules';
import { rateTablesSaga } from '@/store/originator/sagas/rate-tables';
import { supplyTemplateSaga } from '@/store/originator/sagas/supply-template';
import { supplyTemplateApplicationsSaga } from '@/store/originator/sagas/supply-template-application';
import { initializeTemplateCenterSaga } from '@/store/pages/template-center/sagas';
import { userPagesSaga } from '@/store/pages/users/sagas';
import { premiumLendersReducer } from '@/store/premium-lenders/reducer';
import { premiumLendersSaga } from '@/store/premium-lenders/sagas';
import type { PremiumLendersState } from '@/store/premium-lenders/types';
import { reducer as PreSelectRulesReducer } from '@/store/preselect-rule/reducer';
import { preSelectRuleSaga } from '@/store/preselect-rule/sagas';
import type { PreSelectRuleState } from '@/store/preselect-rule/types';
import { reducer as SaasReducer } from '@/store/saas/reducer';
import { saasSaga } from '@/store/saas/sagas';
import type { SaasState } from '@/store/saas/types';
import { sagaDispatcherPipeSaga } from '@/store/saga-dispatcher-flow';
import { LoggerFactory } from '@/utils/logger';

import { getEnv } from '../envWrapper';

import { reducer as BillingCycleReducer } from './billing-cycle/reducer';
import { billingCycleSaga } from './billing-cycle/sagas';
import type { BillingCycleState } from './billing-cycle/types';
import { reducer as ComplianceReducer } from './compliance/reducer';
import { reducer as DataImportsReducer } from './data-imports/reducer';
import { reducer as DemandConfigurationsReducer } from './demand-configurations/reducer';
import { demandConfigurationsSaga } from './demand-configurations/sagas';
import type { DemandConfigurationsState } from './demand-configurations/types';
import { demandReducer as DemandContractsReducer } from './demand-contracts/reducer';
import { demandContractsSaga } from './demand-contracts/sagas';
import type { DemandContractsState } from './demand-contracts/types';
import { reducer as ImportedChannelEventsReducer } from './imported-channel-events/reducer';
import { reducer as ImportedLeadEventsReducer } from './imported-lead-events/reducer';
import { importedLeadEventsSaga } from './imported-lead-events/sagas';
import type { ImportedLeadEventsState } from './imported-lead-events/types';
import { interfaceReducer as InterfaceReducer } from './interface/reducer';
import type { InterfaceState } from './interface/reducer';
import { reducer as MessageReducer } from './message/reducer';
import { messageSaga } from './message/sagas';
import type { MessageState } from './message/types';
import { reducer as OffersReducer } from './offers/reducer';
import { offerSaga } from './offers/sagas';
import type { OfferState } from './offers/types';
import { reducer as OriginatorApiReducer } from './originator/reducer';
import { originatorSaga } from './originator/sagas';
import type { OriginatorState } from './originator/types';
import { reducer as PartnerPageReducer } from './partner-page/reducer';
import { partnerPageSaga } from './partner-page/sagas';
import type { PartnerPageState } from './partner-page/types';
import { reducer as PerformanceSummariesReducer } from './performance-summary/reducer';
import { performanceSummariesSaga } from './performance-summary/sagas';
import type { PerformanceSummariesState } from './performance-summary/types';
import { receivableReducer as ReceivablesReducer } from './receivable/reducer';
import { receivableSaga } from './receivable/sagas';
import type { ReceivableState } from './receivable/types';
import { reducer as SubAccountsReducer } from './subaccount/reducer';
import { subAccountSaga } from './subaccount/sagas';
import type { SubAccountState } from './subaccount/types';
import { reducer as SupplyContractsReducer } from './supply-contracts/reducer';
import { supplyContractsSaga } from './supply-contracts/sagas';
import type { SupplyContractsState } from './supply-contracts/types';
import { reducer as SupplyMigrationReducer } from './supply-migration/reducer';
import { supplyMigrationSaga } from './supply-migration/sagas';
import { reducer as TrustedPartnersReducer } from './trusted-partners/reducer';
import { trustedPartnersSaga } from './trusted-partners/sagas';
import type { TrustedPartnersState } from './trusted-partners/types';
import type { StoreWithSagaTask } from './types';

export interface ApplicationState {
  accounts: AccountState;
  analytics: AnalyticsState;
  authentication: AuthenticationState;
  billingCycles: BillingCycleState;
  compliance: ComplianceState;
  config: ConfigState;
  dataImports: DataImportsState;
  demandConfigurations: DemandConfigurationsState;
  demandContracts: DemandContractsState;
  demandReporting: DemandReportingState;
  featureFlags: FeatureFlagState;
  feedback: FeedbackState;
  importedChannelEvents: ImportedChannelEventsState;
  importedLeadEvents: ImportedLeadEventsState;
  interface: InterfaceState;
  lead: LeadState;
  leadHydration: LeadHydrationState;
  loading: LoadingState;
  message: MessageState;
  notifications: NotificationState;
  offers: OfferState;
  onboardingContract: OnboardingContractState;
  originator: OriginatorState;
  originatorExternal: OriginatorExternalState;
  partnerPages: PartnerPageState;
  payables: PayableState;
  performanceSummaries: PerformanceSummariesState;
  preSelectRules: PreSelectRuleState;
  premiumLenders: PremiumLendersState;
  pricingControls: PricingControlsState;
  receivables: ReceivableState;
  saas: SaasState;
  subAccounts: SubAccountState;
  supplyContracts: SupplyContractsState;
  supplyMigration: SupplyMigrationState;
  trustedPartners: TrustedPartnersState;
  user: UserState;
  whitelist: WhitelistState;
}

// The root reducer is composed of the reducer(s) of each individual store.
const rootReducer = combineReducers({
  accounts: AccountsReducer,
  analytics: AnalyticsReducer,
  authentication: authenticationReducer,
  billingCycles: BillingCycleReducer,
  compliance: ComplianceReducer,
  config: ConfigReducer,
  dataImports: DataImportsReducer,
  demandConfigurations: DemandConfigurationsReducer,
  demandContracts: DemandContractsReducer,
  demandReporting: demandReportingReducer,
  featureFlags: FeatureFlagReducer,
  feedback: FeedbackReducer,
  importedChannelEvents: ImportedChannelEventsReducer,
  importedLeadEvents: ImportedLeadEventsReducer,
  interface: InterfaceReducer,
  lead: LeadReducer,
  leadHydration: LeadHydrationReducer,
  loading: loadingReducer,
  message: MessageReducer,
  notifications: NotificationReducer,
  offers: OffersReducer,
  onboardingContract: OnboardingContractReducer,
  originator: OriginatorApiReducer,
  originatorExternal: originatorExternalReducer,
  partnerPages: PartnerPageReducer,
  payables: PayablesReducer,
  performanceSummaries: PerformanceSummariesReducer,
  preSelectRules: PreSelectRulesReducer,
  premiumLenders: premiumLendersReducer,
  pricingControls: PricingControlsReducer,
  receivables: ReceivablesReducer,
  saas: SaasReducer,
  subAccounts: SubAccountsReducer,
  supplyContracts: SupplyContractsReducer,
  supplyMigration: SupplyMigrationReducer,
  trustedPartners: TrustedPartnersReducer,
  user: UserReducer,
  whitelist: WhitelistReducer,
});

// We `fork()` these tasks so they execute in the background.
export function* rootSaga() {
  yield all([
    fork(accountSaga),
    fork(analyticsSaga),
    fork(billingCycleSaga),
    fork(complianceSaga),
    fork(dataImportsSaga),
    fork(demandConfigurationsSaga),
    fork(demandContractsSaga),
    fork(featureFlagSaga),
    fork(feedbackSaga),
    fork(importedChannelEventsSaga),
    fork(importedLeadEventsSaga),
    fork(leadHydrationSaga),
    fork(leadSaga),
    fork(messageSaga),
    fork(notificationsSagas),
    fork(offerRulesSaga),
    fork(offerSaga),
    fork(onboardingContractSaga),
    fork(originatorSaga),
    fork(partnerPageSaga),
    fork(payableSaga),
    fork(performanceSummariesSaga),
    fork(preSelectRuleSaga),
    fork(pricingControlsSaga),
    fork(rateTablesSaga),
    fork(receivableSaga),
    fork(saasSaga),
    fork(sagaDispatcherPipeSaga),
    fork(subAccountSaga),
    fork(originatorExternalSaga),
    fork(supplyContractsSaga),
    fork(supplyMigrationSaga),
    fork(supplyTemplateApplicationsSaga),
    fork(supplyTemplateSaga),
    fork(trustedPartnersSaga),
    fork(usersSaga),
    fork(whitelistSaga),
    fork(premiumLendersSaga),
    fork(userPagesSaga),
    fork(homeSaga),
    fork(demandReportingSaga),
    fork(initializeTemplateCenterSaga),
    fork(authenticationSaga),
  ]);
}

// eslint-disable-next-line import/no-mutable-exports
export let store: Store<ApplicationState, AnyAction>;

// Middleware is any function that matches:
// ({ getState, dispatch }) => next => action
//
// https://redux.js.org/api/applymiddleware

// Enhancer is any function that matches:
// (next: StoreCreator) => StoreCreator
//
// Intuitively: composable store factories that
// you are discouraged from using because you can
// probably achieve your goal by making middleware.
//
// https://redux.js.org/glossary#store-enhancer

// Factory function to create the Redux store, decorate middleware, etc.
// TODO: figure out how to import options interface from pkg

export const makeStore = (
  preloadedState?: Partial<ApplicationState>
): StoreWithSagaTask<ApplicationState, AnyAction> => {
  const sagaLogger = LoggerFactory('Saga Logger', initialConfigState);

  const sagaMiddleware = createSagaMiddleware({
    context: {
      Router,
    },
    onError: (err) => sagaLogger.error(err),
  });

  const middlewareEnhancer = applyMiddleware(sagaMiddleware);

  const composeEnhancersProd = composeWithDevTools;
  const composeEnhancersDev = composeWithDevToolsDevelopmentOnly({
    trace: true,
    traceLimit: 25,
  });

  /*
    We discovered that the Dev version of Redux Dev Tools works on localhost,
    but not the Dev Env. I'm not sure what Env Var (if any) can be used to
    distinguish between "dev" (localhost) and "dev" (controls-dev). I did,
    however see that we set AUTH_COOKIE_DOMAIN to 'localhost' when developing
    locally. I'm using this temporarily so that Redux Dev Tools will work
    properly on Dev, Stage, QA, and Prod and continue to work properly on
    localhost. The commented out version of "enhancers" should ultimately
    be used, but with something like:

    getEnv().IS_PRODUCTION_ENV || getEnv().IS_DEV_ENV

    // const enhancers = getEnv().IS_PRODUCTION_ENV
    //   ? composeEnhancersProd(middlewareEnhancer)
    //   : composeEnhancersDev(middlewareEnhancer);

  */

  const enhancers =
    getEnv().AUTH_COOKIE_DOMAIN === 'localhost'
      ? composeEnhancersDev(middlewareEnhancer)
      : composeEnhancersProd(middlewareEnhancer);

  store = createStore(rootReducer, preloadedState as any, enhancers);
  const rootSagaTask = sagaMiddleware.run(rootSaga);

  return { ...store, rootSagaTask };
};

export type RootState = StateType<typeof rootReducer>;

let clientStore: StoreWithSagaTask<ApplicationState, AnyAction> | undefined;

export const initializeStore = (preloadedState?: Partial<ApplicationState>) => {
  if (typeof window === 'undefined') {
    return makeStore(preloadedState);
  }
  if (!clientStore) {
    clientStore = makeStore(preloadedState);
  } else if (preloadedState) {
    // Creating a new store instance when preloadedState is provided will lose the running saga tasks and break Redux middleware functionality
    // Consider testing dispatching a hydration action if that is the case instead
    // clientStore.dispatch({ payload: preloadedState, type: HYDRATE });
    clientStore = makeStore({
      ...(clientStore.getState() as ApplicationState),
      ...preloadedState,
    });
  }
  return clientStore;
};

export default makeStore;
